import { useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  TextField,
  Typography,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DashboardLayout from "layout";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "components/Toast";

import { saveAirports, AirportData } from "../../services/airports";
import { Add, Delete, UploadFile } from "@mui/icons-material";

type AirportFormValues = {
  airports: {
    AirportCode: string;
    AirportName: string;
    CityCountry: string;
    City: string;
    Country: string;
  }[];
};

const schema = yup.object().shape({
  airports: yup
    .array()
    .of(
      yup.object().shape({
        AirportCode: yup
          .string()
          .required("Airport code is required")
          .max(3, "Airport code must be 3 characters"),
        AirportName: yup.string().required("Airport name is required"),
        CityCountry: yup.string().required("City and country is required"),
        City: yup.string().required("City is required"),
        Country: yup.string().required("Country is required"),
      }),
    )
    .min(1, "Add at least one airport"),
}) as yup.ObjectSchema<AirportFormValues>;

const cleanImportedAirports = (raw: unknown) => {
  const list: any[] = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as any)?.airports)
      ? (raw as any).airports
      : [raw];

  const cleaned = list.map((item: any) => ({
    AirportCode: String(item?.AirportCode ?? "")
      .trim()
      .toUpperCase(),
    AirportName: String(item?.AirportName ?? item?.Description ?? "").trim(),
    CityCountry: String(item?.CityCountry ?? "").trim(),
    City: String(item?.City ?? "").trim(),
    Country: String(item?.Country ?? "").trim(),
  }));

  return cleaned.filter((item) => item.AirportCode || item.AirportName);
};

const inputLabelSx = {
  fontSize: "14px",
  color: "#344054",
  fontWeight: 600,
  mb: 1,
};

const textFieldSx = {
  bgcolor: "#F9F9F9",
  mt: 2,
  mb: 2,
  py: 1,
  px: 2,
  borderRadius: "10px",
  color: "#101828",
  fontSize: "14px",
};

const Airports = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [importOpen, setImportOpen] = useState(false);
  const [jsonInput, setJsonInput] = useState("");

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AirportFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      airports: [
        {
          AirportCode: "",
          AirportName: "",
          CityCountry: "",
          City: "",
          Country: "",
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "airports",
  });

  const createMutation = useMutation({
    mutationFn: saveAirports,
    onSuccess: (response: any) => {
      showToast(response?.message || "Airports saved successfully", "success");
      queryClient.invalidateQueries({ queryKey: ["airports"] });
      reset();
    },
    onError: (error: any) => {
      showToast(
        error?.response?.data?.message || "Failed to save airports",
        "error",
      );
    },
  });

  const handleSaveImportedAirports = () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonInput);
    } catch {
      showToast(
        "Invalid JSON — please check the format and try again",
        "error",
      );
      return;
    }

    const cleaned = cleanImportedAirports(parsed);
    if (cleaned.length === 0) {
      showToast("No valid airport entries found in that JSON", "error");
      return;
    }

    createMutation.mutate({
      airports: cleaned.map((airport) => ({
        ...airport,
        AirportCode: airport.AirportCode.toUpperCase(),
      })),
    });
    setImportOpen(false);
    setJsonInput("");
  };

  const onSubmit = (data: AirportFormValues) => {
    const payload = {
      airports: data.airports.map((airport) => ({
        ...airport,
        AirportCode: airport.AirportCode.toUpperCase(),
      })),
    };

    createMutation.mutate(payload);
  };

  return (
    <DashboardLayout nextSideBar>
      <Box sx={{ width: "100%", p: { xs: 0, sm: 3 } }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 4 }}
          flexDirection={{ xs: "column", sm: "row" }}
          gap={{ xs: 2, sm: 0 }}
        >
          <Box>
            <Typography
              fontSize="24px"
              fontWeight="600"
              sx={{ mb: 1 }}
              color="#101828"
            >
              Manage Airports
            </Typography>
            <Typography fontSize="14px" color="#475467">
              Add airport information
            </Typography>
          </Box>
          <Button
            type="button"
            variant="outlined"
            startIcon={<UploadFile />}
            onClick={() => setImportOpen(true)}
            sx={{ borderRadius: "999px", px: 3, whiteSpace: "nowrap" }}
          >
            Import JSON
          </Button>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            bgcolor: "white",
            p: { xs: 2, sm: 4 },
            borderRadius: "12px",
            border: "1px solid #EAECF0",
            mb: 4,
          }}
        >
          {fields.map((field, index) => (
            <Box key={field.id} sx={{ mb: 4 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6" fontSize="16px" fontWeight="600">
                  Airport {index + 1}
                </Typography>
                {fields.length > 1 && (
                  <IconButton
                    onClick={() => remove(index)}
                    color="error"
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                )}
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: { xs: 1, sm: 3 },
                }}
              >
                <FormControl variant="standard" fullWidth>
                  <InputLabel shrink sx={inputLabelSx}>
                    Airport Code
                  </InputLabel>
                  <Controller
                    name={`airports.${index}.AirportCode`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder="e.g., ADD"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={textFieldSx}
                        error={!!errors.airports?.[index]?.AirportCode}
                        helperText={
                          errors.airports?.[index]?.AirportCode?.message
                        }
                        inputProps={{
                          maxLength: 3,
                          style: { textTransform: "uppercase" },
                        }}
                      />
                    )}
                  />
                </FormControl>

                <FormControl variant="standard" fullWidth>
                  <InputLabel shrink sx={inputLabelSx}>
                    Airport Name
                  </InputLabel>
                  <Controller
                    name={`airports.${index}.AirportName`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder="e.g., Addis Ababa (ADD)"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={textFieldSx}
                        error={!!errors.airports?.[index]?.AirportName}
                        helperText={
                          errors.airports?.[index]?.AirportName?.message
                        }
                      />
                    )}
                  />
                </FormControl>

                <FormControl variant="standard" fullWidth>
                  <InputLabel shrink sx={inputLabelSx}>
                    City
                  </InputLabel>
                  <Controller
                    name={`airports.${index}.City`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder="e.g., Addis Ababa"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={textFieldSx}
                        error={!!errors.airports?.[index]?.City}
                        helperText={errors.airports?.[index]?.City?.message}
                      />
                    )}
                  />
                </FormControl>

                <FormControl variant="standard" fullWidth>
                  <InputLabel shrink sx={inputLabelSx}>
                    Country
                  </InputLabel>
                  <Controller
                    name={`airports.${index}.Country`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder="e.g., Ethiopia"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={textFieldSx}
                        error={!!errors.airports?.[index]?.Country}
                        helperText={errors.airports?.[index]?.Country?.message}
                      />
                    )}
                  />
                </FormControl>

                <FormControl
                  variant="standard"
                  fullWidth
                  sx={{ gridColumn: { xs: "span 1", sm: "span 2" } }}
                >
                  <InputLabel shrink sx={inputLabelSx}>
                    City and Country
                  </InputLabel>
                  <Controller
                    name={`airports.${index}.CityCountry`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder="e.g., Addis Ababa, Ethiopia"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={textFieldSx}
                        error={!!errors.airports?.[index]?.CityCountry}
                        helperText={
                          errors.airports?.[index]?.CityCountry?.message
                        }
                      />
                    )}
                  />
                </FormControl>
              </Box>
              {index < fields.length - 1 && <Divider sx={{ mt: 4 }} />}
            </Box>
          ))}

          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={2}
            justifyContent="center"
            mt={4}
          >
            <Button
              type="button"
              variant="outlined"
              startIcon={<Add />}
              fullWidth={isMobile}
              onClick={() =>
                append({
                  AirportCode: "",
                  AirportName: "",
                  CityCountry: "",
                  City: "",
                  Country: "",
                })
              }
              sx={{
                borderRadius: "999px",
                px: 4,
              }}
            >
              Add Another
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={createMutation.isPending}
              startIcon={<Add />}
              fullWidth={isMobile}
              sx={{
                bgcolor: createMutation.isPending
                  ? "primary.dark"
                  : "primary.main",
                px: { xs: 4, sm: 8 },
                py: 1.5,
                borderRadius: "999px",
                "&:hover": {
                  bgcolor: createMutation.isPending
                    ? "primary.dark"
                    : "primary.main",
                },
              }}
            >
              {createMutation.isPending ? "Saving..." : "Save Airports"}
            </Button>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Import Airports from JSON</DialogTitle>
        <DialogContent>
          <Typography fontSize="13px" color="#667085">
            Paste a JSON array of airports. Fields <b>AirportCode</b>,{" "}
            <b>AirportName</b> (or <b>Description</b>), <b>CityCountry</b>,{" "}
            <b>City</b>, and <b>Country</b> are used; other fields are
            ignored. Saving here submits the pasted airports directly — the
            form above is not affected.
          </Typography>
          <TextField
            multiline
            rows={isMobile ? 10 : 14}
            fullWidth
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
            placeholder={`[\n  {\n    "AirportCode": "BJS",\n    "Description": "Beijing All Airports (BJS)",\n    "CityCountry": "Beijing, China",\n    "City": "Beijing",\n    "Country": "China"\n  }\n]`}
            sx={{
              mt: 2,
              fontFamily: "monospace",
              "& textarea": { fontFamily: "monospace", fontSize: "13px" },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => {
              setImportOpen(false);
              setJsonInput("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!jsonInput.trim() || createMutation.isPending}
            onClick={handleSaveImportedAirports}
          >
            {createMutation.isPending ? "Saving..." : "Save Airports"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default Airports;
